// Import react elements
import { useContext, useState } from "react";

// Import context
import { AuthContext } from "global/context/AuthContext";

// Import api calls
import { deleteConnectionShippingMethod, fetchConnectionsData } from "api/APIStrichCalls";

// Import context function
import { UserContext } from "global/context/UserContext";

// Import global hooks
import { useToast } from "global/hooks/useToast";

// Import components
import CreateButton from "global/components/CreateButton";
import ToggleSwitch from "global/components/ToggleSwitch";
import { InfoButton } from "global/components/InfoButton";
import HelpButton from "global/components/HelpButton";

// Import utility functions
import { S2B, B2S } from "global/utilities/BooleanConverters";
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";



// Gets unique fulfilment service options from the grouped fulfilment-services response
const GetFulfilmentServiceOptions = (fulfilmentServiceCountries = []) => {
    
    // Track service codes already added
    const usedServiceCodes = {};

    // Store the unique service options
    const serviceOptions = [];

    // Go through each country group returned by core/fulfilment_services
    (fulfilmentServiceCountries || []).forEach((countryGroup) => {

        // Skip malformed country groups
        if (!Array.isArray(countryGroup?.services)) { return; }

        // Go through the services for this country
        countryGroup.services.forEach((service) => {

            // Skip invalid service rows
            if (!service?.service_code) { return; }

            // Skip duplicate service codes
            if (usedServiceCodes[service.service_code]) { return; }

            // Mark this service code as used
            usedServiceCodes[service.service_code] = true;

            // Store the unique service option
            serviceOptions.push(service);

        });

    });

    // Return the unique service options
    return serviceOptions;

};



// This normalises the display name used for countries across slightly different API response shapes.
const GetShippingCountryName = (country) => {

    // Prefer the country name used by the fulfilment-services response
    if (country?.country) { return country.country; }

    // Fall back to the core country name
    if (country?.name) { return country.name; }

    // Fall back to country code
    if (country?.code) { return country.code; }

    // Return an empty value if the row is invalid
    return "";

};



// Gets countries available for the selected fulfilment service code
const GetAvailableShippingCountriesForMethod = (fulfilmentCountries = [], fulfilmentServiceCountries = [], shippingMethod = {}) => {

    // Get selected countries safely
    const selectedCountries = Array.isArray(shippingMethod.countries) ? shippingMethod.countries : [];

    // Get the selected fulfilment service code
    const selectedServiceCode = shippingMethod.code || "";

    // Return no countries until a fulfilment service has been selected
    if (!selectedServiceCode) { return []; }

    // Return only countries that have the selected fulfilment service and are not already selected
    return (fulfilmentServiceCountries || []).filter((countryGroup) => {

        // Get the country name
        const countryName = GetShippingCountryName(countryGroup);

        // Skip invalid countries
        if (!countryName) { return false; }

        // Skip countries already selected on this method
        if (selectedCountries.includes(countryName)) { return false; }

        // Skip malformed country groups
        if (!Array.isArray(countryGroup?.services)) { return false; }

        // Keep countries that have the selected fulfilment service code
        return countryGroup.services.some((service) => service?.service_code === selectedServiceCode);

    });

};


// This finds the first fulfilment service row for a given service code across grouped country results.
const GetFulfilmentServiceForCode = (fulfilmentServiceCountries = [], serviceCode = "") => {

    // Go through each country group returned by core/fulfilment_services
    for (const countryGroup of fulfilmentServiceCountries || []) {

        // Skip malformed country groups
        if (!Array.isArray(countryGroup?.services)) { continue; }

        // Find the matching service in this country group
        const matchingService = countryGroup.services.find((service) => service?.service_code === serviceCode);

        // Return the matching service when found
        if (matchingService) { return matchingService; }

    }

    // No matching service was found
    return null;

};






const RenderConnectionShippingMethods = ({connectionDetails, addingNewConnection, formValues, handleChange}) => {
    
    // Get the authentication token from the AuthContext (determines whether the user is logged in)
    const { token } = useContext(AuthContext);
    
    // Get canonical products and connections from global user data
    const { 
        userData,
        fulfilmentCountries,
        serviceCodes,
    } = useContext(UserContext);

    // Define toast options
    const { toastError, toastSuccess } = useToast();

    // This stores which shipping methods are currently expanded
    const [openShippingMethods, setOpenShippingMethods] = useState({});

    // Find the currency setting value if there is one
    const connectionCurrency = connectionDetails?.currency || null;

    // Store default selected currency based on connection currency, else the account currency
    const [selectedCurrency, ] = useState(connectionCurrency || userData?.currency || "");



    // This opens or closes one shipping method card.
    const handleToggleShippingMethodOpen = (shippingMethodCode) => {

        // Update open state using previous state safely
        setOpenShippingMethods((previousOpenShippingMethods) => ({

            // Keep all previous open states
            ...previousOpenShippingMethods,

            // Toggle the current shipping method
            [shippingMethodCode]: !previousOpenShippingMethods[shippingMethodCode],

        }));

    };


    /*
    // This adds a new shipping method row.
    const handleAddShippingMethod = () => {

        // Create a safe copy of the current shipping methods array
        const currentShippingMethods = Array.isArray(formValues?.shipping_methods)
            ? formValues.shipping_methods
            : [];

        // Add a new editable shipping method
        const updatedShippingMethods = [
            ...currentShippingMethods,
            BuildNewShippingMethod(currencies, serviceCodes),
        ];

        // Push the full updated array into the generic form handler
        handleChange({
            target: {
                name: "shipping_methods",
                value: updatedShippingMethods,
            },
        });

    };

    // This removes one shipping method row.
    const handleRemoveShippingMethod = (index) => {

        // Create a safe copy of the current shipping methods array
        const currentShippingMethods = Array.isArray(formValues?.shipping_methods)
            ? formValues.shipping_methods
            : [];

        // Do not remove the last shipping method
        if (currentShippingMethods.length <= 1) {

            // Toast an error
            toastError({ title: "Error", message: "The store must have at least one shipping method.", autoCloseMs: 6000 });

            // Return early
            return;

        }

        // Remove the selected shipping method
        const updatedShippingMethods = currentShippingMethods.filter((shippingMethod, shippingMethodIndex) => {

            // Keep every method except the selected one
            return shippingMethodIndex !== index;

        });

        // Push the full updated array into the generic form handler
        handleChange({
            target: {
                name: "shipping_methods",
                value: updatedShippingMethods,
            },
        });

    };

    // This resets one shipping method back to its original fulfilment-service defaults
    const _handleResetShippingMethod = (index) => {

        // Get the current shipping methods safely
        const currentShippingMethods = Array.isArray(formValues?.shipping_methods)
            ? formValues.shipping_methods
            : [];

        // Get the selected shipping method
        const currentShippingMethod = currentShippingMethods[index];

        // Stop if the selected shipping method does not exist
        if (!currentShippingMethod) {

            // Return early
            return;

        }

        // Build the default settings for this fulfilment service
        const defaultShippingMethod = GetDefaultShippingMethodForService(
            serviceCodes,
            currentShippingMethod.code,
            currentShippingMethod
        );

        // Stop if the default settings could not be reconstructed
        if (!defaultShippingMethod) {

            // Show an error to the user
            toastError({
                title: "Error",
                message: "The default settings for this shipping method could not be found.",
                autoCloseMs: 6000,
            });

            // Return early
            return;

        }

        // Create the updated shipping methods array
        const updatedShippingMethods = currentShippingMethods.map(
            (shippingMethod, shippingMethodIndex) => {

                // Leave all other shipping methods unchanged
                if (shippingMethodIndex !== index) {

                    // Return the existing method
                    return shippingMethod;

                }

                // Replace this method with its reconstructed defaults
                return defaultShippingMethod;

            }
        );

        // Push the reset shipping methods into the form
        handleChange({
            target: {

                // Update the shipping methods field
                name: "shipping_methods",

                // Pass the updated array
                value: updatedShippingMethods,

            },
        });

    };
    */



    const handleResetShippingMethod = (index, shippingMethodCode) => {

        // Display a confrimation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "<h3>Are you sure?</h3><br/>Resetting the shipping method will revert it to the default settings.",

            // Confirm button title
            "Reset",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (is_confirmed) {

                // Check if the response is TRUE
                if (is_confirmed) {

                    // Delete the shipping method

                    // Make the call
                    const response = await deleteConnectionShippingMethod(token, connectionDetails.id, shippingMethodCode);

                    if (response.success) {

                        // Refresh the connection so the UI updates with the default shipping method
                        const connectionResponse = await fetchConnectionsData(token, connectionDetails.id);

                        // Create a safe copy of the current shipping methods array
                        const currentShippingMethods = Array.isArray(formValues?.shipping_methods)
                            ? formValues.shipping_methods
                            : [];

                        // Remove the selected shipping method
                        const updatedShippingMethods = currentShippingMethods.filter((shippingMethod, shippingMethodIndex) => {

                            // Keep every method except the selected one
                            return shippingMethodIndex !== index;

                        });

                        // Get the default shipping method data
                        const defaultShippingMethod = connectionResponse.results[0]?.shipping_methods?.find((method) => method.code === shippingMethodCode) || {};

                        // Take the current form values shipping methods and apend the returned shipping method we've just reset
                        const newShippingMethods = [...updatedShippingMethods, defaultShippingMethod];

                        // Push the full updated array into the generic form handler
                        handleChange({
                            target: {
                                name: "shipping_methods",
                                value: newShippingMethods,
                            },
                        });

                        // Toast success
                        toastSuccess({ title: "Success", message: "Shipping method reset." });

                    } else {

                        // Toast error
                        toastError({ title: "Error", message: "Could not reset shipping method.", apiErrorMessage: response.error_message, autoCloseMs: 6000 });

                    }
                    
                }

            }

        );

    };







    // This updates one shipping method field.
    const handleChangeShippingMethod = (index, fieldName, fieldValue, isPricing = false) => {

        // Create a safe copy of the current shipping methods array
        const currentShippingMethods = Array.isArray(formValues?.shipping_methods)
            ? formValues.shipping_methods
            : [];
        
        // Check that this isn't the last active shipping method - otherwise we want to block the user from inactivating it
        if (fieldName === "active" && fieldValue === "N") {

            // Filter the methods to only active ones
            const activeShippingMethods = currentShippingMethods.filter((method) => method.active === "Y");

            // If there is only 1 active method 
            if (activeShippingMethods.length === 1) {

                // Toast an error
                toastError({ title: "Error", message: "The store must have at least one active shipping method.", autoCloseMs: 6000 });

                // Return early
                return;

            }

        } 

        // Create a new updated shipping methods array
        const updatedShippingMethods = currentShippingMethods.map((shippingMethod, shippingMethodIndex) => {

            // Only update the matching item
            if (shippingMethodIndex !== index) {

                // Return other items unchanged
                return shippingMethod;

            }

            if (isPricing) {

                // Return the shipping method with updated pricing for the selected currency only
                return {

                    // Keep the existing shipping method data
                    ...shippingMethod,

                    // Update the pricing object
                    pricing: {

                        // Keep all existing currencies
                        ...(shippingMethod.pricing || {}),

                        // Update only the selected currency
                        [selectedCurrency]: {

                            // Keep existing pricing fields for this currency
                            ...(shippingMethod.pricing?.[selectedCurrency] || {}),

                            // Update the specific pricing field
                            [fieldName]: fieldValue,

                        },

                    },

                };

            } else {

                // Return the updated shipping method
                return {

                    ...shippingMethod,

                    // Update the specific field
                    [fieldName]: fieldValue,

                };

            }

        });

        // Push the full updated array into the generic form handler
        handleChange({
            target: {
                name: "shipping_methods",
                value: updatedShippingMethods,
            },
        });

    };



    // This changes the fulfilment service used by a shipping method
    const handleChangeShippingMethodService = (index, serviceCode) => {

        // Create a safe copy of the current shipping methods array
        const currentShippingMethods = Array.isArray(formValues?.shipping_methods)
            ? formValues.shipping_methods
            : [];

        // Get the selected fulfilment service
        const selectedService = GetFulfilmentServiceForCode(serviceCodes, serviceCode);

        // Create a new updated shipping methods array
        const updatedShippingMethods = currentShippingMethods.map((shippingMethod, shippingMethodIndex) => {

            // Only update the matching item
            if (shippingMethodIndex !== index) { return shippingMethod; }

            // Return the updated shipping method
            return {
                ...shippingMethod,
                code: serviceCode,
                name: selectedService?.name || shippingMethod.name || "",
                description: selectedService?.est_delivery || shippingMethod.description || "",
                countries: [],
            };

        });

        // Push the full updated array into the generic form handler
        handleChange({
            target: {
                name: "shipping_methods",
                value: updatedShippingMethods,
            },
        });

    };



    // This adds one country to a shipping method.
    const handleAddShippingMethodCountry = (index, countryName) => {

        // Ignore empty selections
        if (!countryName) { return; }

        // Create a safe copy of the current shipping methods array
        const currentShippingMethods = Array.isArray(formValues?.shipping_methods)
            ? formValues.shipping_methods
            : [];

        // Create a new updated shipping methods array
        const updatedShippingMethods = currentShippingMethods.map((shippingMethod, shippingMethodIndex) => {

            // Only update the matching item
            if (shippingMethodIndex !== index) { return shippingMethod; }

            // Get the existing countries safely
            const currentCountries = Array.isArray(shippingMethod.countries) ? shippingMethod.countries : [];

            // Do not add duplicates
            if (currentCountries.includes(countryName)) { return shippingMethod; }

            // Return the updated shipping method
            return {
                ...shippingMethod,
                countries: [
                    ...currentCountries,
                    countryName,
                ],
            };

        });

        // Push the full updated array into the generic form handler
        handleChange({
            target: {
                name: "shipping_methods",
                value: updatedShippingMethods,
            },
        });

    };



    // This removes one country from a shipping method.
    const handleRemoveShippingMethodCountry = (index, countryName) => {

        // Create a safe copy of the current shipping methods array
        const currentShippingMethods = Array.isArray(formValues?.shipping_methods)
            ? formValues.shipping_methods
            : [];

        // Get the selected shipping method
        const selectedShippingMethod = currentShippingMethods[index];

        // Get the selected method countries safely
        const selectedCountries = Array.isArray(selectedShippingMethod?.countries)
            ? selectedShippingMethod.countries
            : [];

        // Do not allow removing the last country
        if (selectedCountries.length <= 1) {

            // Toast an error
            toastError({
                title: "Error",
                message: "The shipping method needs at least one country.",
                autoCloseMs: 6000,
            });

            // Return early
            return;

        }

        // Create a new updated shipping methods array
        const updatedShippingMethods = currentShippingMethods.map((shippingMethod, shippingMethodIndex) => {

            // Only update the matching item
            if (shippingMethodIndex !== index) { return shippingMethod; }

            // Return the updated shipping method
            return {
                ...shippingMethod,
                countries: selectedCountries.filter((currentCountry) => currentCountry !== countryName),
            };

        });

        // Push the full updated array into the generic form handler
        handleChange({
            target: {
                name: "shipping_methods",
                value: updatedShippingMethods,
            },
        });

    };




    return (

        //* This is the Store Shipping Methods card */}
        <div className="card">

            <div className="card__header connection-details-card__header">

                <h3>Shipping <HelpButton section="stores" code="shipping-methods" label="Shipping Methods" /></h3>

                <span className="hintText">

                    {!addingNewConnection && 
                    
                        <span>Edit shipping prices, free shipping thresholds and countries</span>
                    }

                </span>

            </div>

            <div className="card__content">

                {/* Tell the user they need to save the store before shipping methods will appear */}
                {addingNewConnection &&
                
                    <div>

                        <p>Click save to view and edit shipping methods.</p>

                    </div>
                
                }

                {!addingNewConnection &&


                    <div className="shipping-methods-list__toolbar">

                        {/* Taken out - MVP 
                        <div>

                            Currency: 

                            <select
                                name="visualType" 
                                value={selectedCurrency}
                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                style={{width: "auto", padding: "2px 8px"}}
                            >
                                {["GBP", "USD", "EUR"].map((currencyCode) => (

                                    <option key={currencyCode} value={currencyCode}>
                                        {currencyCode}
                                    </option>

                                ))}

                            </select>
                            
                        </div>
                        

                        <div></div>

                        <div>

                            <CreateButton
                                title="+ Add shipping method"
                                size="s"
                                type="button"
                                onClick={handleAddShippingMethod}
                            />
                            
                        </div>
                        */}

                    </div>

                }


                {/* This is the list of shipping methods */}
                <div className="shipping-methods-list">

                    {formValues?.shipping_methods?.map((shippingMethod, index) => {

                        // This is the unique key used for the row
                        const shippingMethodKey = `${shippingMethod.code}-shipping-method-${index}` || `shipping-method-${index}`;

                        // Get the pricing row for the selected currency
                        //const pricing = shippingMethod.pricing?.[selectedCurrency] || {};

                        // Get the currency symbol for the selected currency
                        //const currencySymbol = currencies?.[selectedCurrency]?.symbol || "";

                        // Get the countries available to add to this method
                        const availableCountries = GetAvailableShippingCountriesForMethod(fulfilmentCountries, serviceCodes, shippingMethod);

                        // Get the available fulfilment service options
                        const fulfilmentServiceOptions = GetFulfilmentServiceOptions(serviceCodes);

                        // Get the selected countries safely
                        const selectedCountries = Array.isArray(shippingMethod.countries) ? shippingMethod.countries : [];

                        // This checks whether the current method is expanded
                        const isOpen = !!openShippingMethods[shippingMethodKey];
                        
                        return (

                            <div
                                key={shippingMethodKey}
                                className="shipping-methods-list__card "    
                            >

                                {/* This is the active toggle */}
                                <div 
                                    className="shipping-methods-list__status cursor-pointer" 
                                    onClick={() => handleToggleShippingMethodOpen(shippingMethodKey)}
                                >

                                    {/* This is the shipping method name */}
                                    <div className="horizontal-align-content-center">

                                        {shippingMethod.name}

                                        {/* - Taken out MVP
                                        <label className="shipping-methods-list__label">

                                            Name

                                        </label>

                                         
                                        <input
                                            name={`shipping_methods_${index}_name`}
                                            type="text"
                                            value={shippingMethod.name || ""}
                                            onChange={(event) => {
                                                handleChangeShippingMethod(index, "name", event.target.value);
                                            }}
                                            onClick={(event) => event.stopPropagation()}
                                        />
                                        */}

                                        {/* <span className="shipping-methods-list__summaryPrice">{currencySymbol}{pricing.price_inc_tax || ""}</span> */}

                                    </div>

                                    <div className="horizontal-align-content-center">

                                        {/* This removes the shipping method  - Taken out MVP
                                        <CreateButton
                                            title="Remove"
                                            size="xs"
                                            invert={true}
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleRemoveShippingMethod(index)
                                            }}
                                        />
                                        */}
                                        <ToggleSwitch
                                            size="s"
                                            value={S2B(shippingMethod.active)}
                                            onChange={(event) => {
                                                handleChangeShippingMethod(index, "active", B2S(event.target.value))
                                            }}
                                        />

                                        {/* This is the expand and collapse icon */}
                                        <span className="shipping-methods-list__open-close">

                                            {isOpen ? "-" : "+"}

                                        </span>

                                    </div>

                                </div>


                                {isOpen && (

                                    <div className="shipping-methods-list__details">

                                        <br/>

                                        {/* This is the editable shipping method row */}
                                        <div className="shipping-methods-list__row">
                                        
                                            {/* This is the fulfilment service selector */}
                                            <div className="shipping-methods-list__service-field">

                                                <label className="shipping-methods-list__label">

                                                    Fulfilment service

                                                </label>

                                                <select
                                                    value={shippingMethod.code || ""}
                                                    onChange={(event) => {
                                                        handleChangeShippingMethodService(index, event.target.value);
                                                    }}
                                                    onClick={(event) => event.stopPropagation()}
                                                    disabled
                                                >
                                                    
                                                    <option value="">

                                                        Select service

                                                    </option>

                                                    {fulfilmentServiceOptions.map((service) => (

                                                        <option key={service.service_code} value={service.service_code}>

                                                            {service.name || service.service_code}

                                                        </option>

                                                    ))}

                                                </select>

                                            </div>

                                            {/* This is the tax-inclusive shipping price - MVP
                                            <div className="shipping-methods-list__price-field">

                                                <label className="shipping-methods-list__label">
                                                    Price incl. tax
                                                </label>

                                                <div className="shipping-methods-list__currency-symbol">

                                                    {currencySymbol}

                                                    <input
                                                        
                                                        name={`shipping_methods_${index}_price_inc_tax`}
                                                        type="text"
                                                        value={pricing.price_inc_tax || ""}
                                                        onChange={(event) => {
                                                            event.stopPropagation();
                                                            handleChangeShippingMethod(index, "price_inc_tax", event.target.value, true)
                                                        }}
                                                    />

                                                </div>

                                            </div>
                                            */}
                                            {/* This is the free shipping threshold - MVP
                                            <div className="shipping-methods-list__price-field">

                                                <label className="shipping-methods-list__label">
                                                    Free over
                                                </label>

                                                <div className="shipping-methods-list__currency-symbol">

                                                    {currencySymbol}

                                                    

                                                </div>

                                            </div>
                                            */}

                                            {/* This is the fulfilment service selector */}
                                            <div className="shipping-methods-list__service-field">

                                                <label className="shipping-methods-list__label">

                                                    Pricing Mode

                                                </label>

                                                <div className="horizontal-align-content-center">

                                                    <select 
                                                        value={shippingMethod.pricing_mode || ""}
                                                        //onChange={(event) => {
                                                        //    handleChangeShippingMethodService(index, event.target.value);
                                                        //}}
                                                        onClick={(event) => event.stopPropagation()}
                                                        disabled
                                                    >

                                                        <option key="cost" value="cost">

                                                            Cost

                                                        </option>

                                                    </select>
                                                    
                                                    <InfoButton
                                                        shortLabel="Cost"
                                                        description="Cost pricing mode ensures that the shipping will always breakeven. Shipping price is calculated at the checkout based on destination country, tax requirements, and currency."
                                                    />

                                                </div>

                                            </div>

                                        </div>

                                        {/* This is the shipping method description */}
                                        <div className="shipping-methods-list__description-row">

                                            <label className="shipping-methods-list__label">

                                                Description

                                            </label>

                                            <input
                                                name={`shipping_methods_${index}_description`}
                                                type="text"
                                                value={shippingMethod.description || ""}
                                                onChange={(event) =>
                                                    handleChangeShippingMethod(index, "description", event.target.value)
                                                }
                                                disabled
                                            />

                                        </div>

                                        {/* This is the countries row */}
                                        <div className="shipping-methods-list__countries-row">

                                            <label className="shipping-methods-list__label">

                                                Shipping countries

                                            </label>
                                            
                                            <div className="shipping-methods-list__country-pills">

                                                {selectedCountries.map((countryName) => (

                                                    <button
                                                        key={countryName}
                                                        type="button"
                                                        className="shipping-methods-list__country-pill"
                                                        onClick={() => handleRemoveShippingMethodCountry(index, countryName)}
                                                        title="Remove country"
                                                    >

                                                        {countryName}

                                                        <span className="shipping-methods-list__country-pill-remove">
                                                            ×
                                                        </span>

                                                    </button>

                                                ))}

                                                {selectedCountries.length === 0 && (

                                                    <span className="shipping-methods-list__empty-countries warning-message">

                                                        No countries added

                                                    </span>

                                                )}

                                            </div>

                                            <select
                                                className="shipping-methods-list__country-select"
                                                value=""
                                                onChange={(event) => handleAddShippingMethodCountry(index, event.target.value)}
                                                disabled={availableCountries.length === 0}
                                            >

                                                <option value="">

                                                    {availableCountries.length > 0 ? "Add country" : "No more countries"}

                                                </option>

                                                {availableCountries.map((country) => {

                                                    // Get the country name
                                                    const countryName = GetShippingCountryName(country);

                                                    // Return the country option
                                                    return (

                                                        <option key={country.code || countryName} value={countryName}>

                                                            {countryName}

                                                        </option>

                                                    );

                                                })}

                                            </select>

                                        </div>

                                        <div className="horizontal-align-content-center flex-justify-left-right">

                                            <div></div>

                                            {shippingMethod.source === "user" &&

                                                <CreateButton
                                                    title="Reset"
                                                    size="s"
                                                    invert={true}
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleResetShippingMethod(index, shippingMethod.code);
                                                    }}
                                                />

                                            }

                                        </div>
                                        
                                    </div>

                                )}

                            </div>

                        );

                    })}

                </div>

            </div>

        </div>

    )


}; export default RenderConnectionShippingMethods;